import { AuthTypeConfig, AuthTypeResolverError, getAuth } from '@garner-health/components-auth';
import { AsyncResult, failure, success } from '@garner-health/lib-ts-common';
import constants from 'app-constants';

import { ApiAllocation, apiAllocationClient, identityClient } from '~/clients';
import createLogger from '~/logging';

const log = createLogger(__filename);

type Scope = 'core' | 'core_plus' | 'core_demo' | 'core_plus_demo' | 'hcsc_compliant' | 'hcsc_compliant_demo';

type Claims = {
  email: string;
  clientId: number;
  careNavigatorId: number;
};

export async function getTokenClaims(): Promise<Claims> {
  const claims = await getAuth().getTokenClaims();
  const clientId = Number(claims['custom:client_id']);
  const careNavigatorId = Number(claims['custom:care_navigator_id']);
  if (!Number.isFinite(clientId) || !Number.isFinite(careNavigatorId)) {
    throw new Error('Token is missing client or care navigator ID');
  }
  return { email: claims.email, clientId, careNavigatorId };
}

// Every provider search needs the plan, so share one lookup per client instead of refetching each time
const allocationRequests = new Map<number, Promise<ApiAllocation>>();

export function getDataProAllocation(clientId: number): Promise<ApiAllocation> {
  const cached = allocationRequests.get(clientId);
  if (cached) return cached;

  const request = apiAllocationClient.getDataProApiAllocation(clientId).then(allocation => {
    if (!allocation) throw new Error('No DataPro API allocation found for client');
    return allocation;
  });
  request.catch(() => {
    if (allocationRequests.get(clientId) === request) allocationRequests.delete(clientId);
  });
  allocationRequests.set(clientId, request);
  return request;
}

export function clearDataProAllocationCache() {
  allocationRequests.clear();
}

export type Plan = 'core' | 'core_plus' | 'hcsc_compliant';

export type ScopeInfo = {
  isDemo: boolean;
  plan: Plan;
};

const scopeInfoMap: Record<Scope, ScopeInfo> = {
  core: { isDemo: false, plan: 'core' },
  hcsc_compliant: { isDemo: false, plan: 'hcsc_compliant' },
  core_plus: { isDemo: false, plan: 'core_plus' },
  core_demo: { isDemo: true, plan: 'core' },
  hcsc_compliant_demo: { isDemo: true, plan: 'hcsc_compliant' },
  core_plus_demo: { isDemo: true, plan: 'core_plus' },
};

function isScope(value: string | undefined): value is Scope {
  return value !== undefined && Object.keys(scopeInfoMap).includes(value);
}

export async function getScopeInfo(): Promise<ScopeInfo> {
  const { clientId } = await getTokenClaims();
  const allocation = await getDataProAllocation(clientId);
  const scope = allocation.scope.split(':')[1];
  if (!isScope(scope)) throw new Error(`Unrecognized DataPro scope: ${allocation.scope}`);
  return scopeInfoMap[scope];
}

export async function authTypeResolver(emailDomain: string): AsyncResult<AuthTypeConfig, AuthTypeResolverError> {
  const userPoolId = constants.authConfig.cognito.userPoolId;

  try {
    const providers = await identityClient.getIdentityProviders({ emailDomain, userPoolId });

    if (providers.length > 1) {
      log.warn({ userPoolId, strEmailDomain: emailDomain }, 'Only one identity provider was expected');
    }

    if (providers.length) {
      return success({
        type: 'SSO',
        providerName: providers[0].name,
      });
    }

    return success({
      type: 'PASSWORD',
    });
  } catch (err) {
    log.error({ err, userPoolId, strEmailDomain: emailDomain }, 'Error getting identity providers');
    return failure('UNKNOWN');
  }
}
